import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText } from '../../components/common';
import { FiSend, FiPaperclip, FiMoreVertical } from 'react-icons/fi';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'expert';
  timestamp: Date;
  status: 'sent' | 'delivered' | 'read';
}

// Mock expert data
const mockExpert = {
  name: 'Dr. Priya Sharma',
  image: null,
  isOnline: true,
};

// Mock messages
const initialMessages: Message[] = [
  {
    id: '1',
    text: 'Hello! Thanks for booking a session with me. How can I help you today?',
    sender: 'expert',
    timestamp: new Date(Date.now() - 5 * 60 * 1000),
    status: 'read',
  },
  {
    id: '2',
    text: 'Hi! I wanted to discuss my investment portfolio and get some advice on diversification.',
    sender: 'user',
    timestamp: new Date(Date.now() - 4 * 60 * 1000),
    status: 'read',
  },
  {
    id: '3',
    text: 'Of course! I\'d be happy to help. Could you tell me a bit about your current portfolio allocation?',
    sender: 'expert',
    timestamp: new Date(Date.now() - 3 * 60 * 1000),
    status: 'read',
  },
];

const ChatScreen: React.FC = () => {
  const navigate = useNavigate();
  useParams<{ bookingId: string }>();
  const [searchParams] = useSearchParams();
  const expertName = searchParams.get('expert') || mockExpert.name;

  const colors = useAppSelector((state) => state.theme.theme);
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [inputText, setInputText] = useState('');
  const [sessionTime, setSessionTime] = useState(30 * 60); // 30 minutes in seconds
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Countdown timer for session
    const timer = setInterval(() => {
      setSessionTime((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatMessageTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const handleSend = () => {
    if (!inputText.trim()) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      text: inputText.trim(),
      sender: 'user',
      timestamp: new Date(),
      status: 'sent',
    };

    setMessages((prev) => [...prev, newMessage]);
    setInputText('');

    // Simulate expert response
    setTimeout(() => {
      const expertResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Thanks for sharing that. Let me think about the best approach for your situation...',
        sender: 'expert',
        timestamp: new Date(),
        status: 'read',
      };
      setMessages((prev) => [...prev, expertResponse]);
    }, 2000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEndSession = () => {
    navigate('/experts');
  };

  return (
    <div
      className="flex flex-col h-screen"
      style={{ backgroundColor: colors.backgroundColor }}
    >

      {/* Header */}
      <div
        className="flex items-center p-4 border-b"
        style={{ borderColor: colors.borderColor }}
      >
        <button onClick={() => navigate(-1)} className="mr-3">
          <CText type="B16" color={colors.textColor}>←</CText>
        </button>

        {/* Expert Info */}
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center mr-3"
          style={{ backgroundColor: colors.primary }}
        >
          <CText type="B14" color={colors.white}>
            {expertName.charAt(0)}
          </CText>
        </div>
        <div className="flex-1">
          <CText type="M14" color={colors.textColor}>
            {expertName}
          </CText>
          <div className="flex items-center gap-1">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: colors.green }}
            />
            <CText type="r12" color={colors.grayScale500}>
              Online
            </CText>
          </div>
        </div>

        {/* Session Timer */}
        <div
          className="px-3 py-1 rounded-full mr-2"
          style={{
            backgroundColor: sessionTime < 300 ? colors.alertColor + '20' : colors.primary + '20',
          }}
        >
          <CText
            type="M12"
            color={sessionTime < 300 ? colors.alertColor : colors.primary}
          >
            {formatTime(sessionTime)}
          </CText>
        </div>

        <button className="p-2">
          {React.createElement(FiMoreVertical as any, { size: 20, color: colors.textColor })}
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex mb-4 ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className="max-w-[75%] rounded-2xl px-4 py-2"
              style={{
                backgroundColor: message.sender === 'user' ? colors.primary : colors.inputBackground,
                borderBottomRightRadius: message.sender === 'user' ? 4 : 16,
                borderBottomLeftRadius: message.sender === 'expert' ? 4 : 16,
              }}
            >
              <CText
                type="r14"
                color={message.sender === 'user' ? colors.white : colors.textColor}
              >
                {message.text}
              </CText>
              <div className="flex justify-end mt-1 gap-1">
                <CText
                  type="r10"
                  color={message.sender === 'user' ? colors.white + '80' : colors.grayScale500}
                >
                  {formatMessageTime(message.timestamp)}
                </CText>
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Session Time Warning */}
      {sessionTime === 0 && (
        <div
          className="p-3 text-center"
          style={{ backgroundColor: colors.alertColor + '20' }}
        >
          <CText type="M14" color={colors.alertColor}>
            Session ended. Thank you for using our service!
          </CText>
          <button
            className="mt-2 px-4 py-2 rounded-lg"
            style={{ backgroundColor: colors.primary }}
            onClick={handleEndSession}
          >
            <CText type="M14" color={colors.white}>
              Back to Experts
            </CText>
          </button>
        </div>
      )}

      {/* Input Area */}
      {sessionTime > 0 && (
        <div
          className="p-4 border-t flex items-center gap-2"
          style={{ borderColor: colors.borderColor }}
        >
          <button className="p-2">
            {React.createElement(FiPaperclip as any, { size: 20, color: colors.grayScale500 })}
          </button>

          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="flex-1 px-4 py-3 rounded-full outline-none"
            style={{
              backgroundColor: colors.inputBackground,
              color: colors.textColor,
            }}
          />

          <button
            onClick={handleSend}
            disabled={!inputText.trim()}
            className="p-3 rounded-full"
            style={{
              backgroundColor: inputText.trim() ? colors.primary : colors.grayScale500,
            }}
          >
            {React.createElement(FiSend as any, { size: 18, color: colors.white })}
          </button>
        </div>
      )}
    </div>
  );
};

export default ChatScreen;
